/**
 * 🛒 CART PAGE - Gemini Design
 * Beautiful cart with promo codes and styling
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Minus, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { useCartStore } from '../store';
import styles from './Cart.module.css';

const TOPPINGS = [
  { id: 'choc_sprinkles', name: 'Chocolate Sprinkles' },
  { id: 'rainbow_sprinkles', name: 'Rainbow Sprinkles' },
  { id: 'crushed_nuts', name: 'Crushed Nuts' },
  { id: 'hot_fudge', name: 'Hot Fudge' },
  { id: 'caramel', name: 'Caramel Drizzle' },
];

// Map item IDs to their images
const itemImages: Record<string, string> = {
  'FM001': '/softserve-tran.png', // Classic Mango
  'FM002': '/softserve-swirl.png', // Tropical Twist
  'FM003': '/softserve-transchocdip.png', // Choc Dip Cone
  'FM004': '/softserve-tranvanillalicious.png', // Vanillalicious
};

const getItemImage = (menuItemId: string) => {
  return itemImages[menuItemId] || '/softserve-tran.png';
};

export function CartPage() {
  const navigate = useNavigate();
  const items = useCartStore((state) => state.items);
  const removeItem = useCartStore((state) => state.removeItem);
  const updateQuantity = useCartStore((state) => state.updateQuantity);
  const updateToppings = useCartStore((state) => state.updateToppings);
  const updateSize = useCartStore((state) => state.updateSize);
  const updateContainer = useCartStore((state) => state.updateContainer);
  const subtotal = useCartStore((state) => state.subtotal());
  const serviceFee = useCartStore((state) => state.serviceFee);
  const total = useCartStore((state) => state.total());

  const [expandedItem, setExpandedItem] = useState<string | null>(null);

  if (items.length === 0) {
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <button className={styles.backButton} onClick={() => navigate(-1)}>
            <ArrowLeft size={24} />
          </button>
          <h1 className={styles.title}>Your Cart</h1>
          <div style={{ width: 24 }} />
        </header>
        
        <div className={styles.emptyState}>
          <span className={styles.emptyEmoji}>🍦</span>
          <h2 className={styles.emptyTitle}>Your cart is empty!</h2>
          <p className={styles.emptySubtitle}>Add some delicious treats</p>
          <button 
            className={styles.browseButton}
            onClick={() => navigate('/menu')}
          >
            Browse Menu
          </button>
        </div>
      </div>
    );
  }

  const getItemDescription = (item: any) => {
    const parts = [];
    if (item.size) parts.push(item.size === 'large' ? 'Large' : 'Small');
    if (item.container) parts.push(item.container === 'cone' ? 'Cone' : 'Cup');
    return parts.join(', ') || 'Classic';
  };

  const getToppingNames = (toppingIds: string[] = []) => {
    return toppingIds.map(id => TOPPINGS.find(t => t.id === id)?.name || id);
  };

  const toggleTopping = (itemId: string, toppingId: string, currentToppings: string[] = []) => {
    const newToppings = currentToppings.includes(toppingId)
      ? currentToppings.filter(t => t !== toppingId)
      : [...currentToppings, toppingId];
    updateToppings(itemId, newToppings);
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          <ArrowLeft size={24} />
        </button>
        <h1 className={styles.title}>Your Cart</h1>
        <div style={{ width: 24 }} />
      </header>

      {/* Cart Items */}
      <div className={styles.itemsList}>
        {items.map((item) => (
          <div key={item.id} className={styles.cartItem}>
            <div className={styles.itemImage}>
              <img
                src={getItemImage(item.menuItemId)}
                alt={item.name}
                className={styles.itemImg}
              />
            </div>

            <div className={styles.itemDetails}>
              <div className={styles.itemHeader}>
                <h3 className={styles.itemName}>{item.name}</h3>
                <button
                  className={styles.deleteButton}
                  onClick={() => removeItem(item.id)}
                >
                  <Trash2 size={18} />
                </button>
              </div>
              <p className={styles.itemMods}>{getItemDescription(item)}</p>

              {/* Toppings section */}
              {item.toppings && item.toppings.length > 0 && (
                <div className={styles.toppingsPreview}>
                  {getToppingNames(item.toppings).map((name, i) => (
                    <span key={i} className={styles.toppingTag}>{name}</span>
                  ))}
                </div>
              )}

              {/* Edit options button */}
              <button
                className={styles.editToppingsBtn}
                onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
              >
                {expandedItem === item.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                Edit options
              </button>

              {/* Expanded editor */}
              {expandedItem === item.id && (
                <div className={styles.toppingsEditor}>
                  {/* Size selector */}
                  <div className={styles.optionGroup}>
                    <span className={styles.optionLabel}>Size</span>
                    <div className={styles.toggleGroup}>
                      <button
                        className={`${styles.toggleBtn} ${item.size === 'small' ? styles.toggleActive : ''}`}
                        onClick={() => updateSize(item.id, 'small')}
                      >
                        Small
                      </button>
                      <button
                        className={`${styles.toggleBtn} ${item.size === 'large' ? styles.toggleActive : ''}`}
                        onClick={() => updateSize(item.id, 'large')}
                      >
                        Large (+$2)
                      </button>
                    </div>
                  </div>

                  {/* Container selector */}
                  <div className={styles.optionGroup}>
                    <span className={styles.optionLabel}>Container</span>
                    <div className={styles.toggleGroup}>
                      <button
                        className={`${styles.toggleBtn} ${item.container === 'cone' ? styles.toggleActive : ''}`}
                        onClick={() => updateContainer(item.id, 'cone')}
                      >
                        🍦 Cone
                      </button>
                      <button
                        className={`${styles.toggleBtn} ${item.container === 'cup' ? styles.toggleActive : ''}`}
                        onClick={() => updateContainer(item.id, 'cup')}
                      >
                        🥤 Cup
                      </button>
                    </div>
                  </div>

                  {/* Toppings */}
                  <div className={styles.optionGroup}>
                    <span className={styles.optionLabel}>Toppings (+$1.50 each)</span>
                    <div className={styles.toppingsList}>
                      {TOPPINGS.map((topping) => (
                        <label key={topping.id} className={styles.toppingOption}>
                          <input
                            type="checkbox"
                            checked={item.toppings?.includes(topping.id) || false}
                            onChange={() => toggleTopping(item.id, topping.id, item.toppings)}
                            className={styles.toppingCheckbox}
                          />
                          <span className={styles.toppingCheckmark}>
                            {item.toppings?.includes(topping.id) && '✓'}
                          </span>
                          <span className={styles.toppingName}>{topping.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className={styles.itemFooter}>
                <div className={styles.quantityControls}>
                  <button
                    className={styles.quantityButton}
                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                  >
                    <Minus size={16} />
                  </button>
                  <span className={styles.quantityText}>{item.quantity}</span>
                  <button
                    className={styles.quantityButton}
                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                  >
                    <Plus size={16} />
                  </button>
                </div>

                <span className={styles.itemPrice}>
                  ${((item.price + (item.toppings?.length || 0) * 1.5) * item.quantity).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Summary */}
      <div className={styles.summary}>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>Subtotal</span>
          <span className={styles.summaryValue}>${subtotal.toFixed(2)}</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>Service Fee</span>
          <span className={styles.summaryValue}>${serviceFee.toFixed(2)}</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.totalLabel}>TOTAL</span>
          <span className={styles.totalValue}>${total.toFixed(2)}</span>
        </div>
        
        <button 
          className={styles.checkoutButton}
          onClick={() => navigate('/checkout')}
        >
          CHECKOUT - ${total.toFixed(2)}
        </button>
        
        <button 
          className={styles.addMoreLink}
          onClick={() => navigate('/menu')}
        >
          🍦 Add more items
        </button>
      </div>
    </div>
  );
}
